import download from 'downloadjs';
import queryString from 'query-string';
import { delay } from 'redux-saga';
import {
  all,
  call,
  put,
  race,
  select,
  spawn,
  take,
  takeLatest,
  takeEvery,
} from 'redux-saga/effects';

import { createStandaloneToast, theme } from '@frontend/legacy-components';

import {
  PART_STATUSES,
  PROCESSING_CATEGORIES,
  VIEWER_MODES,
} from 'constants/all';

import { chatApp } from 'modules/common/chat/data/actions';
import { CHANNEL_TYPES } from 'modules/common/chat/data/constants';
import { PAGE_SOURCE } from 'modules/common/chat/data/constants';
import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';
import { materialWidget } from 'modules/common/forms/materialWidget/data/actions';
import { fileUploadSaga } from 'modules/common/modals/uploadDetailsModal/data/sagas';
import { geometricAnalysisQueue } from 'modules/common/viewer/cadViewer/data/geometricAnalysisData/actions';
import { viewer } from 'modules/common/viewer/data/actions';
import { pdfSearch } from 'modules/common/viewer/pdfViewer/data/actions';
import { quotePartViewer } from 'modules/quotePartViewer/data/actions';
import {
  getActiveComponentId,
  getQuoteNum,
  getQuoteRevision,
  getIsQuotePartViewer,
  getQuoteItemId,
} from 'modules/quotePartViewer/data/reducer';
import { broadcastPartDescriptorChange } from 'modules/quotes/quoteDetailsPage/hooks/useSyncQpvAndBaqTabs';
import { getBestSetupPartFileUuid } from 'modules/viewerPage/apps/quotePartViewerApps/partSetup/components/PartSetupStepper';
import { geometricInterrogation } from 'modules/viewerPage/geometricInterrogation/data/actions';

import partLibraryService from 'services/partLibraryService';
import partService from 'services/partService';

import { getHomePage } from 'store/app/selectors';
import { PAGE_REQUEST } from 'store/app/types';
import { types as materialTypes } from 'store/materials/actions';
import { getHasFetched } from 'store/materials/reducer';
import {
  getMaterialClassByMaterialId,
  getMaterialFamilyByMaterialId,
} from 'store/materials/reducer';
import { initMaterialStore } from 'store/materials/sagas';
import {
  getLeadSource,
  getLeadSourceSignUpFlow,
  getFeatureFlagValue,
} from 'store/session/selectors';
import type { ActionWithPayload } from 'store/types';

import history from 'utils/browserHistory';
import { isStatusInProgress } from 'utils/partUtils';

import {
  downloadTechnicalDataPackage,
  notifyPackageTooLarge,
  partDetails,
  partLibraryViewerPage,
  partFileStatuses,
  removeSupportingFile,
  types,
  uploadSupportingFiles,
  requestAccessToSharedTechDataPackage,
  type PartDescriptorsUpdateRequestPayload,
  type DownloadTechnicalDataPackageRequestPayload,
  type UploadSupportingFilesRequestPayload,
  type PartFileGetPayload,
  type SetActiveFilePayload,
  RequestAccessToPartPayload,
  PartDetailsRequestPayload,
  PartMasterDetailsUpdateRequestPayload,
  RemoveSupportingFileRequestPayload,
  SetPartPanelStatePayload,
} from './actions';
import { APPS_PANEL_STATES, ERROR_CODES, PART_PANEL_STATES } from './constants';
import {
  doesUserOwnPart,
  getActiveFileUUID,
  getActivePart,
  getActivePartFile,
  getActivePartUUID,
  getCadFileCountForActivePart,
  getPartByUUID,
  getPartFileByUUID,
  getPartFileInterrogationStatuses,
  getPartFileProcessingStatuses,
  getPartMasterUuid,
  getPartMasterVersion,
  getTechnicalDataPackage,
  getViewerMode,
} from './reducer';

const { toast } = createStandaloneToast({ theme });

export default function* root() {
  // Use takeEvery because we want saga to run on both part descriptor input blur and export controlled cb click
  yield takeEvery(
    types.PART_DESCRIPTORS_UPDATE_REQUEST,
    watchUpdatePartDescriptors,
  );
  yield takeEvery(
    types.PART_MASTER_DETAILS_UPDATE_REQUEST,
    watchUpdatePartMaster,
  );
  yield takeLatest(
    types.REMOVE_SUPPORTING_FILE_REQUEST,
    watchRemoveSupportingFile,
  );
  yield takeLatest(
    types.DOWNLOAD_TECHNICAL_DATA_PACKAGE_REQUEST,
    watchDownloadTechnicalPackage,
  );
  yield takeLatest(
    types.UPLOAD_SUPPORTING_FILES_REQUEST,
    watchSupportingFileUploads,
  );
  yield takeLatest(types.PART_FILE_GET, watchGetPartFile);
  yield takeLatest(types.SET_ACTIVE_UUID, watchSetActiveUUID);
  yield takeLatest(types.VIEWER_PAGE_INIT, finishViewerPageInit);
  yield takeLatest(types.REQUEST_ACCESS_TO_PART, watchRequestAccessToPart);
  yield takeLatest(types.SET_PART_PANEL_STATE, watchSetPanelState);
  yield takeLatest(types.PART_DETAILS_REQUEST, watchGetPartDetails);
  yield spawn(pageWorker);
}

function* pageWorker() {
  // restart "periodicallyCheckTechDataPackageStatuses" saga when
  // PAGE_REQUEST is dispatched
  for (;;) {
    yield race({
      viewerPage: call(periodicallyCheckTechDataPackageStatuses),
      otherPage: take(PAGE_REQUEST),
    });
  }
}

const getUniquePartFiles = function* (partInformation) {
  const crumbs = new Set();
  for (const part of partInformation) {
    const { partFiles } = part;
    for (const partFile of partFiles ?? []) {
      if (!crumbs.has(partFile.uuid)) {
        crumbs.add(partFile.uuid);
        yield partFile;
        for (const child of partFile.assemblyChildFiles) {
          if (!crumbs.has(child.uuid)) {
            crumbs.add(child.uuid);
            yield child;
            //Note: Previous implementation did not recurse into child.assemblyChildFiles
            //Testing indicates assemblyChildFiles is actually the leaf children, which means there is no need to recurse
          }
        }
      }
    }
  }
};

function* finishTechDataPackageUpdate({
  interrogationQueryUpdateRequired,
  shouldInit,
  technicalDataPackage,
}) {
  yield put(
    partLibraryViewerPage.receiveTechnicalDataPackage({
      technicalDataPackage,
    }),
  );

  const { partInformation } = technicalDataPackage;

  const allPartFiles = Array.from(getUniquePartFiles(partInformation));

  yield put(viewer.receivePartFiles({ partFiles: allPartFiles }));

  const activePartUuid: ReturnType<typeof getActivePartUUID> = yield select(
    getActivePartUUID,
  );

  const part = partInformation.find((prt) =>
    activePartUuid ? prt.uuid === activePartUuid : prt.isRootPart,
  );
  const primaryPartFile = part.partFiles.find(
    (partFile) => partFile.uuid === part.primaryFileUuid,
  );

  if (shouldInit) {
    yield all([
      put(partLibraryViewerPage.setActiveFile({ file: primaryPartFile })),
      put(
        viewer.receiveActivePartFileUuid({
          partFileUuid: primaryPartFile.uuid,
          shouldFocus: false,
          shouldPropagateVisibility: true,
        }),
      ),
    ]);
  }

  if (interrogationQueryUpdateRequired) {
    const { materialId } = part;
    let materialQuery = materialId;
    if (materialId) {
      const matFamily = yield select(getMaterialFamilyByMaterialId, materialId);
      const matClass = yield select(getMaterialClassByMaterialId, materialId);
      materialQuery = [matClass.id, matFamily.id, materialId];
    }
    yield put(
      geometricInterrogation.receiveQuery({
        material: materialQuery,
        task: part.processTag,
        useSelectedTask: true,
      }),
    );
  }
}

function* periodicallyCheckTechDataPackageStatuses() {
  yield take(types.VIEWER_PAGE_INIT);
  const isOwner: ReturnType<typeof doesUserOwnPart> = yield select(
    doesUserOwnPart,
  );
  let technicalDataPackage;
  while (true) {
    try {
      const oldProcessingStatuses: ReturnType<
        typeof getPartFileProcessingStatuses
      > = yield select(getPartFileProcessingStatuses);
      const oldInterrogationStatuses: ReturnType<
        typeof getPartFileInterrogationStatuses
      > = yield select(getPartFileInterrogationStatuses);
      const pendingProcessingEntries = Object.values(
        oldProcessingStatuses,
      ).filter((entry) => {
        let isPending = false;
        for (const category of Object.values(PROCESSING_CATEGORIES)) {
          if (isStatusInProgress(entry?.[category]?.status ?? null)) {
            isPending = true;
            break;
          }
        }
        return isPending;
      });
      const pendingInterrogationEntries = Object.values(
        oldInterrogationStatuses,
      ).filter((entry) => isStatusInProgress(entry.status));
      if (
        pendingProcessingEntries.length > 0 ||
        pendingInterrogationEntries.length > 0
      ) {
        const primaryFileUuids = [
          ...new Set(
            pendingProcessingEntries
              .concat(pendingInterrogationEntries)
              .map((entry) => entry.uuid),
          ),
        ];
        const response = yield call(
          partService.checkStatuses,
          primaryFileUuids,
        );
        const { processing, interrogation } = response.statuses;
        const nextProcessingStatuses = {};
        const nextInterrogationStatuses = {};
        for (const i in processing) {
          nextProcessingStatuses[processing[i].uuid] = {
            ...processing[i],
          };
          nextInterrogationStatuses[interrogation[i].uuid] = {
            ...interrogation[i],
          };
        }

        const activePartFileUuid: ReturnType<typeof getActiveFileUUID> =
          yield select(getActiveFileUUID);

        let areAnyDifferentProcessing = false;
        let viewerUpdateRequired = false;
        let shouldInitUpdate = false;

        // TODO: implement fetching resources for indivudal part files using normalizr
        // so we dont have to be so careful about side effects when updating the
        // technical data package. We can instead just perform a simple fetch to
        // grab something simple like a download url or properties
        for (const [partFileUuid, newStatusInfo] of Object.entries(
          nextProcessingStatuses,
        )) {
          const oldStatusInfo = oldProcessingStatuses[partFileUuid];

          if (!oldStatusInfo) {
            areAnyDifferentProcessing = true;
            continue;
          }

          for (const category of Object.values(PROCESSING_CATEGORIES)) {
            if (
              (oldStatusInfo?.[category]?.status ?? null) !==
              (newStatusInfo?.[category]?.status ?? null)
            ) {
              areAnyDifferentProcessing = true;
              break;
            }
          }

          const finishedViewer =
            oldStatusInfo[PROCESSING_CATEGORIES.VIEWER_CREATION].status !==
              PART_STATUSES.FINISHED &&
            newStatusInfo[PROCESSING_CATEGORIES.VIEWER_CREATION].status ===
              PART_STATUSES.FINISHED;

          if (finishedViewer) {
            viewerUpdateRequired = true;

            // if the part that finishes is the primary file of the root part
            // then we need to trigger the init flow
            const partFile: ReturnType<typeof getPartFileByUUID> = yield select(
              getPartFileByUUID,
              partFileUuid,
            );
            if (!partFile) {
              // protect against race conditions of files being loaded in as supporting files
              // this can cause issues with loading in of bodies, so we should revisit this
              // to tighten down race conditions
              continue;
            }
            const part: ReturnType<typeof getPartByUUID> = yield select(
              getPartByUUID,
              partFile.partUuid,
            );

            if (partFile.isPrimaryFile && part.isRootPart) {
              shouldInitUpdate = true;
            }
          }
        }

        let areAnyDifferentInterrogation = false;
        let interrogationQueryUpdateRequired = false;
        for (const [partFileUuid, newStatusInfo] of Object.entries(
          nextInterrogationStatuses,
        )) {
          const oldStatusInfo = oldInterrogationStatuses[partFileUuid];

          if (!oldStatusInfo) {
            areAnyDifferentInterrogation = true;
            continue;
          }

          // TODO: timing considerations
          if (oldStatusInfo.status !== newStatusInfo.status) {
            areAnyDifferentInterrogation = true;
          }

          const finishedInterrogation =
            oldStatusInfo.status !== PART_STATUSES.FINISHED &&
            newStatusInfo.status === PART_STATUSES.FINISHED;

          if (finishedInterrogation) {
            viewerUpdateRequired = true;

            // if this file is the active file, we need to update the interrogation widget query
            // so that the features and feedback are collected and shown
            if (!activePartFileUuid || partFileUuid === activePartFileUuid) {
              yield put(geometricAnalysisQueue.addToStart({ partFileUuid }));
              interrogationQueryUpdateRequired = true;
            }
          }
        }

        const partMasterUuid: ReturnType<typeof getPartMasterUuid> =
          yield select(getPartMasterUuid);
        const partMasterVersion: ReturnType<typeof getPartMasterVersion> =
          yield select(getPartMasterVersion);
        const quoteItemId: ReturnType<typeof getQuoteItemId> = yield select(
          getQuoteItemId,
        );
        const isQuotePartViewer: ReturnType<typeof getIsQuotePartViewer> =
          yield select(getIsQuotePartViewer);

        if (viewerUpdateRequired) {
          if (isOwner) {
            technicalDataPackage = yield call(
              partLibraryService.getTechnicalDataPackage,
              partMasterUuid,
              partMasterVersion
                ? {
                    version: partMasterVersion,
                    quoteItemId: isQuotePartViewer ? quoteItemId : undefined,
                  }
                : {},
            );
          } else {
            technicalDataPackage = yield call(
              partLibraryService.sharedTechDataPackage.get,
              partMasterUuid,
              partMasterVersion ? { version: partMasterVersion } : {},
            );
          }

          yield call(finishTechDataPackageUpdate, {
            interrogationQueryUpdateRequired,
            shouldInit: shouldInitUpdate,
            technicalDataPackage,
            viewerUpdateRequired,
          });
        }

        if (areAnyDifferentProcessing) {
          yield put(
            partFileStatuses.receiveProcessingStatuses({
              processingStatuses: nextProcessingStatuses,
            }),
          );
        }
        if (areAnyDifferentInterrogation) {
          yield put(
            partFileStatuses.receiveInterrogationStatuses({
              interrogationStatuses: nextInterrogationStatuses,
            }),
          );
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      yield call(delay, 1500);
    }
  }
}

function* watchSetPanelState(
  action: ActionWithPayload<
    typeof types.SET_PART_PANEL_STATE,
    SetPartPanelStatePayload
  >,
) {
  try {
    const { partPanelState } = action.payload;
    if (partPanelState === PART_PANEL_STATES.CAD) {
      /*
      For the CAD Tab
      If the current active part file is not a CAD file AND a cad file exists for the active part
      Then set the active part file to be a CAD file
      */
      const activePartFile: ReturnType<typeof getActivePartFile> = yield select(
        getActivePartFile,
      );
      if (!activePartFile?.hasGeometry) {
        const cadFileCount: ReturnType<typeof getCadFileCountForActivePart> =
          yield select(getCadFileCountForActivePart);
        if (cadFileCount) {
          // need to set a cad file as the active part file
          const activePart: ReturnType<typeof getActivePart> = yield select(
            getActivePart,
          );
          const cadFiles =
            activePart?.partFiles.filter(
              ({ isDerivedFromInterrogation, hasGeometry }) =>
                !isDerivedFromInterrogation && hasGeometry,
            ) ?? [];
          const primaryFile = cadFiles.find(
            ({ isPrimaryFile }) => isPrimaryFile,
          );
          if (primaryFile) {
            // prefer to set the default part
            yield put(
              partLibraryViewerPage.setActiveUUID({
                partFileUuid: primaryFile.uuid,
                forceFocus: false,
              }),
            );
          } else {
            // set the first cad file
            yield put(
              partLibraryViewerPage.setActiveUUID({
                partFileUuid: cadFiles[0]?.uuid,
                forceFocus: false,
              }),
            );
          }
        }
      }
    }
  } catch (error) {
    console.error('Error in watch set panel state', error);
  }
}

function* setPartPanelStateForActiveFile() {
  try {
    const activePartFile: ReturnType<typeof getActivePartFile> = yield select(
      getActivePartFile,
    );
    if (activePartFile?.hasGeometry) {
      // if CAD files exist, redirect to the CAD tab
      yield put(
        partLibraryViewerPage.setPartPanelState({
          partPanelState: PART_PANEL_STATES.CAD,
        }),
      );
    } else {
      yield put(
        partLibraryViewerPage.setPartPanelState({
          partPanelState: PART_PANEL_STATES.FILES,
        }),
      );
    }
  } catch (error) {
    console.error('Error in setPartPanelStateForActiveFile', error);
  }
}

function* setPartPanelStateForActivePart() {
  try {
    const activePart: ReturnType<typeof getActivePart> = yield select(
      getActivePart,
    );
    if (activePart?.isAssembly) {
      yield put(
        partLibraryViewerPage.setPartPanelState({
          partPanelState: PART_PANEL_STATES.BOM,
        }),
      );
    } else {
      yield call(setPartPanelStateForActiveFile);
    }
  } catch (error) {
    console.error('Error in setPartPanelStateForActivePart', error);
  }
}

function* setDefaultPartPanelState(
  hasFocusedFile: boolean,
  hasFocusedPart: boolean,
) {
  /*
  Responsible for setting initial active tabs for users.
   */
  try {
    const technicalDataPackage: ReturnType<typeof getTechnicalDataPackage> =
      yield select(getTechnicalDataPackage);

    if (hasFocusedFile) {
      // top priority is to focus the user based on the selected part/file
      yield call(setPartPanelStateForActiveFile);
    } else if ((technicalDataPackage?.partInformation || []).length > 1) {
      if (hasFocusedPart) {
        yield call(setPartPanelStateForActivePart);
      } else {
        // focus on the bom if the technical data package has an assembly
        yield put(
          partLibraryViewerPage.setPartPanelState({
            partPanelState: PART_PANEL_STATES.BOM,
          }),
        );
      }
    } else {
      // finally default to the focused part
      yield call(setPartPanelStateForActiveFile);
    }
  } catch (error) {
    console.error('Failed to set default part panel state', error);
  }
}

function getSelectedFile(partFiles, selectedFileUUID) {
  const primaryFile = partFiles.find((pf) => pf.isPrimaryFile);
  let selectedFile;
  if (!primaryFile) {
    // if the primary file isn't in the technical data package
    // just set the selected file to the first one
    return partFiles.length && partFiles[0];
  } else if (selectedFileUUID) {
    selectedFile = partFiles.find((f) => f.uuid === selectedFileUUID);
  }

  // if the selected file is valid, return that
  // otherwise the selected file is the primary file
  return selectedFile ? selectedFile : primaryFile;
}

export function* initPartLibraryViewer({ location, match }) {
  try {
    let channelType = '';

    yield all([
      put(partLibraryViewerPage.clear()),
      put(viewer.reset()),
      put(materialWidget.reset()),
    ]);

    // get mode and part master uuid from the url
    const { mode, partMasterUUID } = match.params;

    const {
      annotation_id: annotationId,
      channel_id: channelId,
      active_part: activePartUuid,
      material_selected: materialSelected,
      search: printTextSearch,
      selected_file: selectedFileUUID,
      version: partMasterVersion,
      quote_item_id: quoteItemIdString,
      part_setup: partSetupState,
    } = queryString.parse(location.search ?? {});

    const quoteItemId = parseInt(quoteItemIdString ?? '', 10);
    const isQuotePartViewer = yield select(getIsQuotePartViewer);

    let technicalDataPackage;
    // based on the mode, get owner version or shared
    if (mode === VIEWER_MODES.OWNER) {
      yield call(initMaterialStore);
      technicalDataPackage = yield call(
        partLibraryService.getTechnicalDataPackage,
        partMasterUUID,
        partMasterVersion
          ? {
              version: partMasterVersion,
              quoteItemId: isQuotePartViewer ? quoteItemId : undefined,
            }
          : {},
      );
    } else if (mode === VIEWER_MODES.SHARE) {
      try {
        technicalDataPackage = yield call(
          partLibraryService.sharedTechDataPackage.get,
          partMasterUUID,
          partMasterVersion ? { version: partMasterVersion } : {},
        );
        // Only call material APIs if the part is accessible (technicalDataPackage fetch succeeded)
        yield call(initMaterialStore);
      } catch (error) {
        const statusCode = error?.statusCode ?? null;
        const message = error?.message ?? null;
        if (statusCode === 403 && message === ERROR_CODES.MFA_OR_SSO_REQUIRED) {
          window.alert(
            `Two-factor authentication is required.\n\nThe company that has shared this part with you requires share recipients to have two-factor authentication (2FA) or SSO enabled to access their shares.\n\nPlease adjust your settings and then refresh this page to access the shared part.`,
          );
          history.push('/settings/profile/two_factor_authentication');
          return;
        } else if (statusCode === 403) {
          // redirect unauthorized user from page
          window.alert('You do not have permission to view this part.');
          history.push(yield select(getHomePage));
          return;
        } else if (statusCode === 404) {
          yield put(partLibraryViewerPage.error(error));
          history.push('/parts?page=1&tab=SHARED_PARTS');
          return;
        } else {
          throw error;
        }
      }
    }
    const multiLevelBom = yield call(
      partService.getMultiLevelBom,
      partMasterUUID,
      partMasterVersion
        ? {
            version: partMasterVersion,
          }
        : {},
    );

    yield put(partLibraryViewerPage.setMode({ mode }));
    yield put(
      partLibraryViewerPage.receiveTechnicalDataPackage({
        technicalDataPackage,
      }),
    );
    yield put(
      partLibraryViewerPage.receiveMultiLevelBom({
        multiLevelBom,
      }),
    );

    const {
      activePartUuid: replacementActivePartUuid,
      partInformation,
      rootUuid,
      isExpired,
      version: replacementPartMasterVersion,
    } = technicalDataPackage;

    if (isExpired) {
      return;
    }

    // first collect all part files
    const partFileProcessingStatuses = {};
    const partFileInterrogationStatuses = {};
    const allPartFiles = Array.from(getUniquePartFiles(partInformation));

    // collect all part files
    for (const pf of allPartFiles) {
      const { status } = pf;
      const { interrogation, processing } = status;
      partFileProcessingStatuses[pf.uuid] = processing;
      partFileInterrogationStatuses[pf.uuid] = interrogation;
    }

    yield all([
      put(viewer.receivePartFiles({ partFiles: allPartFiles })),
      put(
        partFileStatuses.receiveInterrogationStatuses({
          interrogationStatuses: partFileInterrogationStatuses,
        }),
      ),
      put(
        partFileStatuses.receiveProcessingStatuses({
          processingStatuses: partFileProcessingStatuses,
        }),
      ),
    ]);

    // now loop thru parts looking for the first file to initialize
    // establish currently active part by the query param, the replacement active uuid,
    // or the rootUuid for the part master
    // NOTE: the replacementActivePartUuid is used in the situation someone is using
    // a deprecated viewer link that was copy and pasted from a child assembly component
    const activeUuid = activePartUuid || replacementActivePartUuid || rootUuid;

    // set the active file if specified directly
    for (const part of partInformation) {
      const { partFiles } = part;
      const primaryFile = partFiles.find((pf) => pf.isPrimaryFile);

      // only proceed with init behavior if the part is the activePart
      if (part.uuid === activeUuid) {
        // Can only init if the primary file viewer document is ready
        let processingDone = true;
        let viewerDone = true;
        if (primaryFile) {
          processingDone = !isStatusInProgress(
            partFileProcessingStatuses[primaryFile.uuid][
              PROCESSING_CATEGORIES.PROCESSING
            ].status,
          );
          viewerDone = !isStatusInProgress(
            partFileProcessingStatuses[primaryFile.uuid][
              PROCESSING_CATEGORIES.VIEWER_CREATION
            ].status,
          );
        }

        const canInit = processingDone && viewerDone;

        if (canInit) {
          const selectedFile = partSetupState //has any partSetup state?
            ? getSelectedFile(partFiles, getBestSetupPartFileUuid(part))
            : getSelectedFile(partFiles, selectedFileUUID);
          // only init part viewer if geometric processing is complete
          yield all([
            put(partLibraryViewerPage.setActiveFile({ file: selectedFile })),
            put(
              viewer.receiveActivePartFileUuid({
                partFileUuid: selectedFile.uuid,
                shouldFocus: false,
                shouldPropagateVisibility: false,
                ignoreQueue: true, // set to true to skip adding file to geometricAnalysisQueue
              }),
            ),
          ]);

          const userHasQuotingEnabled = yield select(
            getFeatureFlagValue,
            featureFlagKeys.isQuotingToolAvailable,
          );
          if (materialSelected) {
            yield put(
              partLibraryViewerPage.setAppsPanelState({
                appsPanelState: APPS_PANEL_STATES.ONLINE_METALS_MATERIAL_WIDGET,
              }),
            );
          } else if (!userHasQuotingEnabled) {
            const leadSource = yield select(getLeadSource);
            const leadSourceSignUpFlow = yield select(getLeadSourceSignUpFlow);
            if (leadSource === 'msc' || leadSourceSignUpFlow === 'msc') {
              yield put(
                partLibraryViewerPage.setAppsPanelState({
                  appsPanelState: APPS_PANEL_STATES.MSC_CHAT,
                }),
              );
              channelType = CHANNEL_TYPES.MSC;
            } else if (
              leadSource === 'penn' ||
              leadSourceSignUpFlow === 'penn'
            ) {
              yield put(
                partLibraryViewerPage.setAppsPanelState({
                  appsPanelState: APPS_PANEL_STATES.PENN_AVAILABILITY_WIDGET,
                }),
              );
            }
          }

          const hasFocusedFile = !!selectedFileUUID;
          const hasFocusedPart = !!activePartUuid;

          yield call(setDefaultPartPanelState, hasFocusedFile, hasFocusedPart);

          if (printTextSearch) {
            yield put(pdfSearch.request({ query: printTextSearch }));
          }
        }
        break;
      }
    }

    yield put(
      chatApp.init({
        activeChannelType: channelType,
        annotationId,
        channelId,
        isExportControlled: technicalDataPackage.isExportControlled,
        isOwner: mode === VIEWER_MODES.OWNER,
        partMasterVersion: replacementPartMasterVersion,
        source: PAGE_SOURCE.VIEWER,
        techDataPackageRootUuid: technicalDataPackage.partMasterUuid,
      }),
    );
    yield put(partLibraryViewerPage.init());
  } catch (error) {
    console.error(error);
  }
}

function* watchUpdatePartDescriptors(
  action: ActionWithPayload<
    typeof types.PART_DESCRIPTORS_UPDATE_REQUEST,
    PartDescriptorsUpdateRequestPayload
  >,
) {
  try {
    const oldTechDataPackage = yield select(getTechnicalDataPackage);
    const { partDetails: updatedDetails, partUuid: receivedPartUuid } =
      action.payload;
    const partUuid = yield receivedPartUuid ?? select(getActivePartUUID);
    const nextTechDataPackage = {
      ...oldTechDataPackage,
    };
    const partResponse = yield call(partService.updateForViewerPage, {
      data: updatedDetails,
      partUuid,
    });

    nextTechDataPackage.partInformation = nextTechDataPackage.partInformation
      .filter((prt) => prt.uuid !== partUuid)
      .concat(partResponse);

    yield put(partDetails.updateSuccess(nextTechDataPackage));

    // Update quote item and broadcast with sanitized data so the Part component
    // in the header shows the sanitized part number/revision (not the pre-save paste).
    const isQPV = yield select(getIsQuotePartViewer);
    const quoteItemId = yield select(getQuoteItemId);
    if (isQPV && quoteItemId != null) {
      const activePart = yield select(getActivePart);
      if (activePart && activePart.uuid === partUuid) {
        yield put(
          quotePartViewer.receiveQuoteItemUpdate({
            quoteItem: {
              id: quoteItemId,
              partNumber: partResponse.partNumber,
              revision: partResponse.revision,
            },
          }),
        );
        const quoteNum = yield select(getQuoteNum);
        const quoteRevision = yield select(getQuoteRevision);
        const componentId = yield select(getActiveComponentId);
        broadcastPartDescriptorChange({
          context: {
            isRootComponent: activePart.isRootPart ?? true,
            partUuid,
            componentId: componentId ?? 0,
            quoteItemId,
            quoteNum: quoteNum ?? 0,
            quoteRevision: quoteRevision ?? null,
          },
          payload: {
            partNumber: partResponse.partNumber,
            revision: partResponse.revision,
          },
        });
      }
    }
  } catch (error) {
    console.error(error);
    yield put(partDetails.updateError(error));
  }
}

function* watchUpdatePartMaster(
  action: ActionWithPayload<
    typeof types.PART_MASTER_DETAILS_UPDATE_REQUEST,
    PartMasterDetailsUpdateRequestPayload
  >,
) {
  try {
    const { isExportControlled } = action.payload;
    const oldTechDataPackage: ReturnType<typeof getTechnicalDataPackage> =
      yield select(getTechnicalDataPackage);
    const nextTechDataPackage = {
      ...oldTechDataPackage,
    };

    const partMasterResponse = yield call(partService.updatePartMaster, {
      data: { isExportControlled },
      partMasterUuid: oldTechDataPackage.partMasterUuid,
    });

    nextTechDataPackage.isExportControlled =
      partMasterResponse.isExportControlled;

    yield put(partDetails.updatePartMasterSuccess(nextTechDataPackage));
  } catch (error) {
    console.error(error);
    yield put(partDetails.updateError(error));
  }
}

function* watchDownloadTechnicalPackage(
  action: ActionWithPayload<
    typeof types.DOWNLOAD_TECHNICAL_DATA_PACKAGE_REQUEST,
    DownloadTechnicalDataPackageRequestPayload
  >,
) {
  try {
    const { partUUID } = action.payload;
    const response = yield call(
      partService.downloadTechnicalDataPackage,
      partUUID,
    );
    const statusCode = response.status;
    switch (statusCode) {
      case 'ready': {
        yield call(download, response.downloadUrl);
        yield put(downloadTechnicalDataPackage.success());
        break;
      }
      case 'missing': {
        yield put(downloadTechnicalDataPackage.error(response.message));
        break;
      }
      case 'uploading': {
        yield put(notifyPackageTooLarge(response.message));
        break;
      }
    }
  } catch (error) {
    yield put(downloadTechnicalDataPackage.error(error));
  }
}

function* watchRemoveSupportingFile(
  action: ActionWithPayload<
    typeof types.REMOVE_SUPPORTING_FILE_REQUEST,
    RemoveSupportingFileRequestPayload
  >,
) {
  try {
    const { supportingFileUUID } = action.payload;
    const oldFile: ReturnType<typeof getPartFileByUUID> = yield select(
      getPartFileByUUID,
      supportingFileUUID,
    );
    const partUUID: ReturnType<typeof getActivePartUUID> = yield select(
      getActivePartUUID,
    );
    yield call(partService.removeAttachedFile, supportingFileUUID, {
      partId: partUUID,
    });

    yield put(removeSupportingFile.success({ supportingFileUUID, partUUID }));
    yield call(toast, {
      title: 'Success',
      status: 'success',
      description: 'Supporting file was deleted.',
    });

    // if the active file is the file or a child of the file we just removed, go back to the main part file
    const activeFileUUID: ReturnType<typeof getActiveFileUUID> = yield select(
      getActiveFileUUID,
    );
    if (
      activeFileUUID === supportingFileUUID ||
      oldFile.assemblyChildFiles.find(({ uuid }) => uuid === activeFileUUID)
    ) {
      const activePart: ReturnType<typeof getActivePart> = yield select(
        getActivePart,
      );
      const primaryPartFile = activePart.partFiles.find(
        (partFile) => partFile.uuid === activePart.primaryFileUuid,
      );
      yield all([
        put(partLibraryViewerPage.setActiveFile({ file: primaryPartFile })),
        put(
          viewer.receiveActivePartFileUuid({
            partFileUuid: primaryPartFile.uuid,
            shouldFocus: true,
            shouldPropagateVisibility: true,
          }),
        ),
      ]);
    }
  } catch (error) {
    yield put(removeSupportingFile.error(error));
  }
}

function* watchSupportingFileUploads(
  action: ActionWithPayload<
    typeof types.UPLOAD_SUPPORTING_FILES_REQUEST,
    UploadSupportingFilesRequestPayload
  >,
) {
  const { payload } = action;
  const { file, redactedFrom } = payload;
  try {
    const fileInfoList = yield call(fileUploadSaga, file);
    const uuid = yield select(getActivePartUUID);

    for (const fileInfo of fileInfoList) {
      yield call(uploadSupportingFile, { fileInfo, uuid, redactedFrom });
    }

    //TODO: RECEIVE PART FILE? Not sure how we want to handle this.
    yield put(uploadSupportingFiles.success());
  } catch (error) {
    yield put(uploadSupportingFiles.error(error));
  }
}

/**
 * NB: THIS IS BAD. We want a standalone request that doesn't need to fetch
 * the entire part, but also need a saga that will get supporting
 * files on dispatch of an action. It will also blow up if you're not in Owner mode
 * of the viewer. Shared mode not allowed. Ideally, we have a separate endpoint just
 * for the supporting file data.
 *
 * This saga pulls the latest part, pulls the part file information
 * for a given part and part file, and updates it in the state.
 *
 * To undravel this - we will need to flatten out part file data and have smaller requests
 * to fetch individual pieces of viewer state data.
 */
function* watchGetPartFile(
  action: ActionWithPayload<typeof types.PART_FILE_GET, PartFileGetPayload>,
) {
  try {
    // Grab part file
    const { partFileUuid, partUuid } = action.payload;

    // Grab the viewer mode and warn if not in owner mode
    const mode: ReturnType<typeof getViewerMode> = yield select(getViewerMode);
    if (mode !== VIEWER_MODES.OWNER) {
      console.warn(
        'Cannot refresh owned technical data package in shared mode',
      );
      return;
    }

    // Fetch the new technical data package and set it in the store
    const newPart = yield call(partService.getForViewerPage, { partUuid });

    // Find the part file in the part
    const matchingPartFile = newPart.partFiles.find(
      (partFile) => partFile.uuid === partFileUuid,
    );
    if (matchingPartFile) {
      yield call(insertNewPartFileIntoStore, {
        partFile: matchingPartFile,
        partUuid,
      });
    } else {
      throw new Error(
        `Could not find part file with uuid ${partFileUuid} in part data`,
      );
    }
  } catch (error) {
    console.error(error);

    // Pop up a simple toast
    yield call(toast, {
      title: 'Failed to get supporting file data',
      status: 'error',
    });
  }
}

function* uploadSupportingFile({ fileInfo, uuid, redactedFrom }) {
  try {
    const formData = new FormData();
    formData.append('original', fileInfo.file);
    formData.append('units', fileInfo.units);
    if (fileInfo.thickness) {
      formData.append('thickness', fileInfo.thickness);
    }
    if (redactedFrom) {
      formData.append('redacted_from', redactedFrom);
    }
    const partMasterVersion: ReturnType<typeof getPartMasterVersion> =
      yield select(getPartMasterVersion);
    formData.append('part_master_version', partMasterVersion);
    const result = yield call(partLibraryService.supportingFileUpload, {
      formData,
      uuid,
    });

    yield call(insertNewPartFileIntoStore, {
      partFile: result,
      partUuid: uuid,
    });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

function* insertNewPartFileIntoStore({ partFile, partUuid }) {
  const nextPartFileProcessingStatuses = {
    [partFile.uuid]: partFile.status.processing,
  };
  const nextPartFileInterrogationStatuses = {
    [partFile.uuid]: partFile.status.interrogation,
  };
  yield all([
    put(
      partFileStatuses.receiveInterrogationStatuses({
        interrogationStatuses: nextPartFileInterrogationStatuses,
      }),
    ),
    put(
      partFileStatuses.receiveProcessingStatuses({
        processingStatuses: nextPartFileProcessingStatuses,
      }),
    ),
  ]);

  yield put(
    uploadSupportingFiles.receiveFile({
      supportingFile: partFile,
      uuid: partUuid,
    }),
  );

  yield put(viewer.receivePartFile({ file: partFile }));
}

/*
This is used to update the surrounding form upon focusing on an entry in the assembly tree.
*/
function* watchSetActiveUUID(
  action: ActionWithPayload<typeof types.SET_ACTIVE_UUID, SetActiveFilePayload>,
) {
  try {
    const {
      forceFocus,
      partFileUuid: uuid,
      partUuid: partUuidFromAction,
    } = action.payload;

    const partFile = yield select(getPartFileByUUID, uuid);
    if (!partFile) {
      return;
    }
    const { partUuid: partUuidFromFile, uuid: partFileUuid } = partFile;
    // Prefer the partUuid passed explicitly from the BOM click (accurate when
    // multiple Parts share the same underlying PartFile) over the one stored on
    // the PartFile object (which reflects the first Part that owns the file).
    const partUuid = partUuidFromAction || partUuidFromFile;
    yield all([
      put(
        partLibraryViewerPage.setActiveFile({
          // Override partUuid so the reducer sets activePartUUID to the correct
          // part when multiple Parts share the same underlying PartFile.
          file: { ...partFile, partUuid },
        }),
      ),
      put(
        viewer.receiveActivePartFileUuid({
          partFileUuid,
          shouldFocus: forceFocus,
          shouldPropagateVisibility: forceFocus,
        }),
      ),
    ]);

    if (partUuid && partFile.isPrimaryFile) {
      const part = yield select(getPartByUUID, partUuid);
      // update queries for interrogation
      const { materialId, processTag } = part;
      if (!materialId) {
        yield put(
          geometricInterrogation.receiveQuery({
            material: null,
            task: processTag,
            shouldInit: true,
          }),
        );
      } else {
        const matFamily = yield select(
          getMaterialFamilyByMaterialId,
          materialId,
        );
        const matClass = yield select(getMaterialClassByMaterialId, materialId);
        const materialQuery = [matClass.id, matFamily.id, materialId];
        yield put(
          geometricInterrogation.receiveQuery({
            material: materialQuery,
            task: processTag,
            shouldInit: true,
          }),
        );
      }
    } else {
      yield all([
        put(
          geometricInterrogation.receiveQuery({
            material: null,
            task: null,
            shouldInit: true,
          }),
        ),
        put(geometricInterrogation.cancelEditInterrogationSetup()),
      ]);
    }
  } catch (error) {
    console.error('Error in watchSetActiveUuid', error);
  }
}

/*
Responsible for establishing first custom interrogation query for the geometric interrogation
widget. Needs to wait for the material store to be initialized and active part/partfile to be set,
which is why it is broken out into its own saga listening to the init action.
*/
function* finishViewerPageInit() {
  const part: ReturnType<typeof getActivePart> = yield select(getActivePart);
  if (!part) return;
  const { materialId, processTag } = part;
  if (!materialId) {
    yield put(
      geometricInterrogation.receiveQuery({
        material: null,
        task: processTag,
        shouldInit: true,
      }),
    );
  } else {
    const isMaterialStoreReady: ReturnType<typeof getHasFetched> = yield select(
      getHasFetched,
    );
    if (!isMaterialStoreReady) {
      const winner = yield race({
        ok: take(materialTypes.INIT_SUCCESS),
        bad: take(materialTypes.INIT_FAILURE),
      });
      if (winner.bad) {
        yield put(
          geometricInterrogation.receiveQuery({
            material: [null, null, materialId],
            task: processTag,
            shouldInit: true,
          }),
        );
      } else {
        const matFamily = yield select(
          getMaterialFamilyByMaterialId,
          materialId,
        );
        const matClass = yield select(getMaterialClassByMaterialId, materialId);
        const materialQuery = [matClass.id, matFamily.id, materialId];
        yield put(
          geometricInterrogation.receiveQuery({
            material: materialQuery,
            task: processTag,
            shouldInit: true,
          }),
        );
      }
    } else {
      const matFamily = yield select(getMaterialFamilyByMaterialId, materialId);
      const matClass = yield select(getMaterialClassByMaterialId, materialId);
      const materialQuery = [matClass.id, matFamily.id, materialId];
      yield put(
        geometricInterrogation.receiveQuery({
          material: materialQuery,
          task: processTag,
          shouldInit: true,
        }),
      );
    }
  }
}

function* watchRequestAccessToPart(
  action: ActionWithPayload<
    typeof types.REQUEST_ACCESS_TO_PART,
    RequestAccessToPartPayload
  >,
) {
  try {
    const { partMasterUuid, version } = action.payload;
    yield call(
      partLibraryService.sharedTechDataPackage.requestAccess,
      partMasterUuid,
      { version },
    );
    yield put(requestAccessToSharedTechDataPackage.success());
  } catch {
    yield put(requestAccessToSharedTechDataPackage.error());
  }
}

function* watchGetPartDetails(
  action: ActionWithPayload<
    typeof types.PART_DETAILS_REQUEST,
    PartDetailsRequestPayload
  >,
) {
  try {
    const { partUuid } = action.payload;
    const partResponse = yield call(partService.getForViewerPage, { partUuid });

    const oldTechDataPackage: ReturnType<typeof getTechnicalDataPackage> =
      yield select(getTechnicalDataPackage);
    const nextTechDataPackage = {
      ...oldTechDataPackage,
    };
    nextTechDataPackage.partInformation = nextTechDataPackage.partInformation
      .filter((prt) => prt.uuid !== partUuid)
      .concat(partResponse);
    yield put(partDetails.updateSuccess(nextTechDataPackage));
  } catch (error) {
    console.error('Error in watchGetPartDetails', error);
  }
}
